import { useState, useEffect, useMemo } from 'react';
import type { AiProfile, Stats } from '@oz/rules';
import {
  MapSpritePicker,
  sheetOptions,
  sheetUrl,
  useImageCatalog,
} from '../../components/SpritePicker.js';
import {
  fetchAdminContent,
  saveAdminContent,
  deleteAdminContent,
  type AdminItem,
} from '../../admin/contentApi.js';
import { ContentOrigin } from '../../admin/ContentOrigin.js';
import { CONDITION_OPTIONS } from '../../admin/vocab.js';

export interface EnemyDef {
  readonly id: number;
  readonly name: string;
  readonly battler: string;
  /**
   * A peça que anda no tabuleiro.
   *
   * Opcional porque o RPG Maker não dá sprite de mapa a inimigo: lá o monstro
   * só existe na tela de batalha. Num tático ele caminha pelo mapa como
   * qualquer unidade, e sem esta escolha todo monstro importado entrava em
   * campo com o mesmo aldeão genérico. Ausente, o padrão continua valendo.
   */
  readonly mapSprite?: { sheet: string; index: number };
  /**
   * Quantas casas a criatura ocupa no tabuleiro.
   *
   * Ausente significa "deduza do desenho" — que é o que o jogo sempre fez, e o
   * que acerta na maioria. A escolha existe para os casos em que a régua erra:
   * um chefe desenhado pequeno que precisa pesar como chefe, ou um battler
   * enorme de uma criatura que deveria caber numa casa. A área declarada é
   * regra de tabuleiro: é ela que bloqueia passagem, que é alcançada por uma
   * lança e que precisa ser cercada.
   */
  readonly size?: { w: number; h: number };
  readonly bases: Stats;
  readonly move: number;
  /**
   * O que o golpe da criatura pode causar, venha de que arma vier — a peçonha
   * do gnomo, o hálito de fogo da salamandra. O pipeline tira das habilidades
   * do RPG Maker; aqui se ajusta.
   */
  readonly inflicts?: readonly { readonly condition: string; readonly chance: number }[];
  readonly aiProfile: AiProfile;
  readonly exp: number;
  readonly gold: number;
  readonly drops?: number[];
}

const STAT_LABELS: Record<keyof Stats, string> = {
  hp: 'PV (Vida)',
  str: 'FOR (Força)',
  mag: 'MAG (Magia)',
  skl: 'HAB (Acerto)',
  spd: 'VEL (Esquiva)',
  lck: 'SOR (Sorte)',
  def: 'DEF (Física)',
  res: 'RES (Mágica)',
};

const STAT_COLORS: Record<keyof Stats, string> = {
  hp: '#22c55e',
  str: '#ef4444',
  mag: '#a855f7',
  skl: '#3b82f6',
  spd: '#06b6d4',
  lck: '#eab308',
  def: '#f97316',
  res: '#ec4899',
};

const AI_PROFILES: { id: AiProfile; label: string; desc: string }[] = [
  { id: 'agressivo', label: '⚔️ Agressivo', desc: 'Ataca qualquer alvo no alcance e avança se vir inimigo.' },
  { id: 'cacador', label: '🎯 Caçador', desc: 'Prioriza alvos frágeis ou que não podem revidar.' },
  { id: 'chefe', label: '👑 Chefe de Fase', desc: 'Permanece no trono/forte sem se mover, contra-ataca quem chegar.' },
  { id: 'guardiao', label: '🛡️ Guardião de Área', desc: 'Só ataca se um invasor entrar no seu raio de patrulha.' },
  { id: 'defensivo', label: '🛡️ Defensivo', desc: 'Mantém formação e prioriza contra-ataques.' },
  { id: 'patrulha', label: '🚶 Patrulha', desc: 'Move-se por rotas pré-definidas no mapa.' },
];

/*
 * Lista de reserva, e não o catálogo: os nomes de verdade vêm do manifesto,
 * que inclui os battlers dos jogos importados. Estes cobrem o intervalo até o
 * manifesto chegar — e o caso em que ele não chega.
 */
const PRESET_BATTLERS = [
  'Goblin',
  'Gnome',
  'Orc',
  'Dragon',
  'Berserker',
  'Birdman',
  'Blackknight',
  'Caitsith',
  'Captain',
  'Crab',
  'Crow',
  'Darkelf',
  'Demon',
  'Demoncount',
  'Evilgod',
  'Foxman',
  'Gatekeeper',
  'Harpy',
  'Mimic',
  'Minotaur',
  'Sahagin',
  'Skeleton',
  'Slime',
  'Werewolf',
  'Zombie',
];

/**
 * As áreas que o painel oferece, da menor para a maior.
 *
 * Vão até 3×3 porque é o maior corpo que ainda deixa jogar num mapa de 25
 * casas de lado: um bicho de 4×4 come um sexto da largura do tabuleiro e não
 * há como cercá-lo. `null` é a régua automática, e continua sendo o padrão.
 */
const BODY_SIZES: readonly { id: string; size: { w: number; h: number } | null; label: string; hint: string }[] = [
  { id: 'auto', size: null, label: 'Automático', hint: 'Deduzido do tamanho do battler — o que o jogo sempre fez.' },
  { id: '1x1', size: { w: 1, h: 1 }, label: '1 × 1', hint: 'Uma casa, como qualquer soldado.' },
  { id: '2x1', size: { w: 2, h: 1 }, label: '2 × 1', hint: 'Duas casas de largura: montarias e bichos deitados.' },
  { id: '1x2', size: { w: 1, h: 2 }, label: '1 × 2', hint: 'Duas casas de altura: criaturas altas e esguias.' },
  { id: '2x2', size: { w: 2, h: 2 }, label: '2 × 2', hint: 'Quatro casas: dragões, golems, chefes de porte.' },
  { id: '3x2', size: { w: 3, h: 2 }, label: '3 × 2', hint: 'Seis casas, deitado — serpentes e feras enormes.' },
  { id: '3x3', size: { w: 3, h: 3 }, label: '3 × 3', hint: 'Nove casas: o maior corpo que ainda dá para cercar.' },
];

function sizeIdOf(size: { w: number; h: number } | undefined): string {
  if (!size) return 'auto';
  const found = BODY_SIZES.find((b) => b.size && b.size.w === size.w && b.size.h === size.h);
  return found?.id ?? 'auto';
}

const DEFAULT_BASES: Stats = {
  hp: 18,
  str: 7,
  mag: 1,
  skl: 6,
  spd: 6,
  lck: 4,
  def: 6,
  res: 3,
};

export function EnemyEditor(): JSX.Element {
  const [items, setItems] = useState<AdminItem<EnemyDef>[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [filterText, setFilterText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const [name, setName] = useState('');
  const [battler, setBattler] = useState('Goblin');
  const [mapSprite, setMapSprite] = useState({ sheet: '', index: 0 });
  const [bodyId, setBodyId] = useState('auto');
  const [move, setMove] = useState(5);
  const [aiProfile, setAiProfile] = useState<AiProfile>('agressivo');
  /** A condição que o golpe da criatura deixa, e a chance por golpe certeiro. */
  const [inflictCondition, setInflictCondition] = useState<string>('');
  const [inflictChance, setInflictChance] = useState(20);
  const [exp, setExp] = useState(15);
  const [gold, setGold] = useState(10);
  const [bases, setBases] = useState<Stats>(DEFAULT_BASES);
  const [isNew, setIsNew] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await fetchAdminContent<EnemyDef>('enemies');
      setItems(data);
      if (data.length > 0 && selectedId === null && data[0]) {
        selectItem(data[0]);
      }
    } catch (e) {
      setMessage({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, []);

  const selectItem = (item: AdminItem<EnemyDef>) => {
    setSelectedId(item.contentId);
    setIsNew(false);
    setName(item.name || item.data.name || '');
    setBattler(item.data.battler || 'Goblin');
    setMapSprite({
      sheet: item.data.mapSprite?.sheet ?? '',
      index: item.data.mapSprite?.index ?? 0,
    });
    setBodyId(sizeIdOf(item.data.size));
    setMove(item.data.move ?? 5);
    setAiProfile(item.data.aiProfile || 'agressivo');
    const inflict = item.data.inflicts?.[0];
    setInflictCondition(inflict?.condition ?? '');
    setInflictChance(inflict?.chance ?? 20);
    setExp(item.data.exp ?? 10);
    setGold(item.data.gold ?? 5);
    setBases(item.data.bases ? { ...item.data.bases } : DEFAULT_BASES);
    setMessage(null);
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsNew(true);
    setName('Novo Monstro');
    setBattler('Goblin');
    setMapSprite({ sheet: '', index: 0 });
    setBodyId('auto');
    setMove(5);
    setAiProfile('agressivo');
    setInflictCondition('');
    setInflictChance(20);
    setExp(20);
    setGold(15);
    setBases({ ...DEFAULT_BASES });
    setMessage(null);
  };

  const handleBaseChange = (stat: keyof Stats, val: number) => {
    setBases((prev) => ({ ...prev, [stat]: Math.max(0, val) }));
  };

  const chosenBody = BODY_SIZES.find((b) => b.id === bodyId)?.size ?? null;

  const handleSave = async () => {
    if (!name.trim()) {
      setMessage({ type: 'err', text: 'O nome do monstro é obrigatório.' });
      return;
    }

    setSaving(true);
    setMessage(null);

    /*
     * O que o formulário não mostra tem de sobreviver ao salvamento.
     *
     * A gravação substitui o registro inteiro pelo que sobe daqui, e este
     * formulário não edita `drops`, `actions` nem a marca de qual jogo o
     * monstro veio. Montando o pacote em cima da ficha atual, salvar um ajuste
     * de EXP deixa de apagar a tabela de loot e as habilidades importadas.
     */
    const existing = items.find((it) => it.contentId === selectedId)?.data;

    const payload: { -readonly [K in keyof EnemyDef]?: EnemyDef[K] } & { name: string } = {
      ...(isNew || !existing ? {} : existing),
      name: name.trim(),
      battler,
      // Sem folha escolhida o campo não vai: gravar `{sheet: ''}` seria dizer
      // "este monstro tem sprite nenhum", quando o que se quer dizer é "use o
      // padrão do time" — e quem lê o registro não distingue os dois.
      ...(mapSprite.sheet ? { mapSprite } : {}),
      // "Automático" não grava campo nenhum: gravar `null` diria "sem área",
      // e o que se quer dizer é "meça você" — quem lê o registro não distingue
      // os dois, e é a mesma razão de o sprite de mapa ausente ficar ausente.
      ...(chosenBody ? { size: chosenBody } : {}),
      move: Number(move),
      aiProfile,
      inflicts: inflictCondition
        ? [{ condition: inflictCondition, chance: Math.max(1, Math.min(100, Number(inflictChance))) }]
        : [],
      exp: Number(exp),
      gold: Number(gold),
      bases,
    };

    /*
     * Voltar ao padrão precisa APAGAR o campo, e não só deixar de escrevê-lo.
     *
     * Com a ficha atual servindo de base, um campo que o formulário decide não
     * gravar continua lá do jeito que estava — e limpar a folha de sprite ou
     * escolher "Automático" não teria efeito nenhum ao salvar.
     */
    if (!mapSprite.sheet) delete payload.mapSprite;
    if (!chosenBody) delete payload.size;

    const targetId = isNew ? null : selectedId;
    const res = await saveAdminContent<EnemyDef>('enemies', targetId, payload);

    setSaving(false);
    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao salvar inimigo.' });
    } else {
      setMessage({ type: 'ok', text: `Inimigo "${name}" salvo com sucesso!` });
      await loadData();
      if (res.item) {
        selectItem(res.item);
      }
    }
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm(`Tem certeza que deseja excluir o monstro ID #${selectedId} (${name})?`)) {
      return;
    }

    setSaving(true);
    const res = await deleteAdminContent('enemies', selectedId);
    setSaving(false);

    if (!res.ok) {
      setMessage({ type: 'err', text: res.message ?? 'Erro ao excluir inimigo.' });
    } else {
      setMessage({ type: 'ok', text: `Inimigo #${selectedId} excluído.` });
      const remaining = items.filter((it) => it.contentId !== selectedId);
      setItems(remaining);
      if (remaining.length > 0 && remaining[0]) {
        selectItem(remaining[0]);
      } else {
        handleNew();
      }
    }
  };

  const filteredItems = items.filter(
    (it) =>
      it.name.toLowerCase().includes(filterText.toLowerCase()) ||
      String(it.contentId).includes(filterText) ||
      (it.data.aiProfile && it.data.aiProfile.toLowerCase().includes(filterText.toLowerCase())),
  );

  const currentItem = items.find((it) => it.contentId === selectedId);

  // Um battler de monstro pode estar na galeria de frente (`enemies`) ou na de
  // combate lateral (`sv_enemies`) — e um jogo importado costuma trazer só uma
  // das duas. Procurar nas duas é o que faz a prévia aparecer em vez de um
  // quadrado vazio.
  const battlerArt = useImageCatalog('enemies', 'sv_enemies');
  const battlerNames = useMemo(
    () => sheetOptions(battlerArt, battler, PRESET_BATTLERS),
    [battlerArt, battler],
  );

  return (
    <div className="admin-db-container">
      <aside className="admin-sidebar-list">
        <div className="admin-list-header">
          <input
            type="text"
            className="admin-search-input"
            placeholder="🔍 Filtrar monstros..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          <button type="button" className="admin-btn-primary admin-btn-new" onClick={handleNew}>
            + Novo Inimigo
          </button>
        </div>

        {loading ? (
          <div className="admin-loading-indicator">Carregando inimigos…</div>
        ) : (
          <div className="admin-items-scroll">
            {filteredItems.map((it) => {
              const active = !isNew && it.contentId === selectedId;
              return (
                <button
                  key={it.contentId}
                  type="button"
                  className={`admin-list-item-btn ${active ? 'active' : ''}`}
                  onClick={() => selectItem(it)}
                >
                  <span className="admin-item-id">
                    {String(it.contentId).padStart(3, '0')}:
                  </span>
                  <span className="admin-item-name">{it.name}</span>
                  {it.isCustom && <span className="admin-badge-custom">Custom</span>}
                  <span className="admin-item-meta">{it.data.aiProfile}</span>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <main className="admin-main-form">
        <div className="admin-form-top-bar">
          <div className="admin-form-title-area">
            <h2>
              {isNew ? '✨ Criando Novo Inimigo' : `👹 Inimigo #${selectedId}: ${name}`}
            </h2>
            {currentItem && (
              <ContentOrigin item={currentItem} />
            )}
          </div>

          <div className="admin-form-actions">
            {!isNew && (
              <button
                type="button"
                className="admin-btn-danger"
                onClick={() => void handleDelete()}
                disabled={saving}
              >
                🗑️ Excluir
              </button>
            )}
            <button
              type="button"
              className="admin-btn-save"
              onClick={() => void handleSave()}
              disabled={saving}
            >
              {saving ? 'Salvando…' : '💾 Salvar Alterações'}
            </button>
          </div>
        </div>

        {message && (
          <div className={`admin-alert-banner ${message.type === 'ok' ? 'success' : 'error'}`}>
            {message.text}
          </div>
        )}

        <div className="admin-form-body-grid">
          <div className="admin-section-box">
            <h3 className="admin-section-title">Comportamento & Recompensas</h3>

            <div className="admin-form-row">
              <label>Nome da Unidade:</label>
              <input
                type="text"
                className="admin-input-text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: Ogro Furioso"
              />
            </div>

            <div className="admin-form-row">
              <label>Perfil de Inteligência Artificial (IA):</label>
              <select
                className="admin-select"
                value={aiProfile}
                onChange={(e) => setAiProfile(e.target.value as AiProfile)}
              >
                {AI_PROFILES.map((ai) => (
                  <option key={ai.id} value={ai.id}>
                    {ai.label}
                  </option>
                ))}
              </select>
              <span className="admin-helper-text">
                {AI_PROFILES.find((a) => a.id === aiProfile)?.desc}
              </span>
            </div>

            <div className="admin-form-row">
              <label>O golpe desta criatura causa:</label>
              <select
                className="admin-select"
                value={inflictCondition}
                onChange={(e) => setInflictCondition(e.target.value)}
              >
                <option value="">— nada além do dano —</option>
                {CONDITION_OPTIONS.map((c) => (
                  <option key={c.id} value={c.id} title={c.summary}>
                    {c.label} ({c.turns} {c.turns === 1 ? 'turno' : 'turnos'})
                  </option>
                ))}
              </select>
              {inflictCondition !== '' && (
                <>
                  <label style={{ marginTop: '8px' }}>Chance por golpe (%):</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="admin-input-number"
                    value={inflictChance}
                    onChange={(e) => setInflictChance(Number(e.target.value) || 1)}
                  />
                </>
              )}
              <span className="admin-helper-text">
                Vale com qualquer arma que a criatura empunhe, e some ao que a arma já causar.
                A importação traduz isto das habilidades do jogo de origem.
              </span>
            </div>

            <div className="admin-grid-3col" style={{ marginTop: '12px' }}>
              <div className="admin-form-row">
                <label>Movimento:</label>
                <input
                  type="number"
                  min="0"
                  max="12"
                  className="admin-input-number"
                  value={move}
                  onChange={(e) => setMove(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>EXP Concedida:</label>
                <input
                  type="number"
                  min="0"
                  max="500"
                  className="admin-input-number"
                  value={exp}
                  onChange={(e) => setExp(Number(e.target.value))}
                />
              </div>

              <div className="admin-form-row">
                <label>Ouro (Gold):</label>
                <input
                  type="number"
                  min="0"
                  max="5000"
                  className="admin-input-number"
                  value={gold}
                  onChange={(e) => setGold(Number(e.target.value))}
                />
              </div>
            </div>
          </div>

          <div className="admin-section-box">
            <h3 className="admin-section-title">Aparência: Combate & Tabuleiro</h3>

            <div className="admin-enemy-preview-container">
              <div
                className="admin-enemy-preview-box"
                style={{
                  backgroundImage: `url(${sheetUrl(battlerArt, 'enemies', battler)})`,
                  backgroundSize: 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center bottom',
                }}
              />
              <div className="admin-enemy-select-box">
                <label>Arquivo do Battler:</label>
                <select
                  className="admin-select"
                  value={battler}
                  onChange={(e) => setBattler(e.target.value)}
                >
                  {battlerNames.map((b) => (
                    <option key={b} value={b}>
                      {b}.webp
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  className="admin-input-text"
                  placeholder="Ou digite o nome do arquivo..."
                  value={battler}
                  onChange={(e) => setBattler(e.target.value)}
                  style={{ marginTop: '8px' }}
                />
              </div>

              <MapSpritePicker
                label="Sprite no Mapa (Tabuleiro)"
                emptyLabel="(usar o battler)"
                value={mapSprite}
                onChange={setMapSprite}
                fallbackArt={
                  battlerArt[battler]
                    ? {
                        url: sheetUrl(battlerArt, 'enemies', battler),
                        width: battlerArt[battler]!.width,
                        height: battlerArt[battler]!.height,
                      }
                    : null
                }
                fallbackNote="o próprio battler, no tamanho da casa"
              />
            </div>

            <p className="admin-helper-text" style={{ marginTop: '10px' }}>
              O battler é a imagem do combate; o sprite de mapa é a peça que a criatura move pelo
              tabuleiro. Sem folha escolhida, é o próprio battler que vai ao tabuleiro — ajustado
              ao tamanho da casa, como a prévia mostra.
            </p>

            <div className="admin-form-row admin-body-size">
              <label>Espaço no Tabuleiro (casas ocupadas):</label>
              <div className="admin-body-size-grid">
                {BODY_SIZES.map((body) => (
                  <button
                    key={body.id}
                    type="button"
                    title={body.hint}
                    className={`admin-body-size-btn ${bodyId === body.id ? 'active' : ''}`}
                    onClick={() => setBodyId(body.id)}
                  >
                    <span
                      className="admin-body-size-shape"
                      style={{
                        gridTemplateColumns: `repeat(${body.size?.w ?? 1}, 1fr)`,
                        gridTemplateRows: `repeat(${body.size?.h ?? 1}, 1fr)`,
                      }}
                      aria-hidden="true"
                    >
                      {body.size
                        ? Array.from({ length: body.size.w * body.size.h }, (_, i) => (
                            <i key={i} />
                          ))
                        : <em>auto</em>}
                    </span>
                    <span className="admin-body-size-label">{body.label}</span>
                  </button>
                ))}
              </div>
              <span className="admin-helper-text">
                {BODY_SIZES.find((b) => b.id === bodyId)?.hint}
                {chosenBody && chosenBody.w * chosenBody.h > 1 && (
                  <>
                    {' '}A criatura passa a bloquear as {chosenBody.w * chosenBody.h} casas, e
                    precisa de espaço livre para se mover e para nascer no mapa.
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="admin-section-box admin-section-span2">
            <h3 className="admin-section-title">Atributos Base (Estatísticas de Batalha)</h3>
            <p className="admin-helper-text">
              Valores numéricos de combate direto da criatura.
            </p>

            <div className="admin-stats-grid">
              {(Object.keys(STAT_LABELS) as (keyof Stats)[]).map((st) => (
                <div key={st} className="admin-stat-field">
                  <span className="admin-stat-tag" style={{ borderLeftColor: STAT_COLORS[st] }}>
                    {st.toUpperCase()}
                  </span>
                  <input
                    type="number"
                    min="0"
                    max="99"
                    className="admin-stat-input"
                    value={bases[st] ?? 0}
                    onChange={(e) => handleBaseChange(st, Number(e.target.value))}
                  />
                  <span className="admin-stat-label-small">{STAT_LABELS[st]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
